import {
  ComponentPropsWithoutRef,
  CSSProperties,
  FC,
  useRef,
  useState,
} from 'react';
import { useMotionValue, motion } from 'motion/react';
import { cn } from '@wwe-portal/ui/lib/lib';
import { Slottable, Root } from '@radix-ui/react-slot';
import styles from './shining.module.css';

const MAX_ROTATE_DEG = 15;
const TRANSLATE_Z_RATIO = 10;
const MOUSE_LEAVE_TIMEOUT = 500;

export interface ShiningProps {
  children: React.ReactNode;
  asChild?: boolean;
  options?: {
    maxRotateDeg?: number;
    translateZRatio?: number;
    mouseLeaveTimeout?: number;
  };
  maskImage?: string;
  containerProps?: ComponentPropsWithoutRef<'div'>;
  lightType?: 'white' | 'rainbow';
  shiningProps?: Pick<ComponentPropsWithoutRef<'div'>, 'style'>;
  enabled: boolean;
}

export const Shining: FC<ShiningProps> = ({
  children,
  asChild,
  options: overrideOptions,
  containerProps,
  maskImage,
  lightType,
  shiningProps,
  enabled: enabledProp,
}) => {
  const options = {
    maxRotateDeg: MAX_ROTATE_DEG,
    translateZRatio: TRANSLATE_Z_RATIO,
    mouseLeaveTimeout: MOUSE_LEAVE_TIMEOUT,
    ...overrideOptions,
  };
  const Comp = asChild ? Root : 'div';
  const containerRef = useRef<HTMLDivElement>(null);
  const cardShineRef = useRef<HTMLDivElement>(null);

  const [isMouseEntered, setIsMouseEntered] = useState(false);
  const enabled = isMouseEntered && enabledProp;

  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);
  const translateZ = useMotionValue(0);

  const mouseLeaveTimeout = useRef<NodeJS.Timeout | null>(null);

  const changeCardShineVariables = ({
    backgroundX,
    backgroundY,
    mouseX,
    mouseY,
    pointerFromCenter,
  }: {
    backgroundX: number;
    backgroundY: number;
    mouseX: number;
    mouseY: number;
    pointerFromCenter: number | string;
  }) => {
    if (!cardShineRef.current || !enabled) return;
    cardShineRef.current.style.setProperty('--background-y', `${backgroundY}%`);
    cardShineRef.current.style.setProperty('--background-x', `${backgroundX}%`);
    cardShineRef.current.style.setProperty('--pointer-x', `${mouseX}%`);
    cardShineRef.current.style.setProperty('--pointer-y', `${mouseY}%`);
    cardShineRef.current.style.setProperty(
      '--pointer-from-center',
      pointerFromCenter.toString()
    );
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (mouseLeaveTimeout.current) {
      clearTimeout(mouseLeaveTimeout.current);
    }
    if (!containerRef.current) return;
    const { left, top, width, height } =
      containerRef.current.getBoundingClientRect();

    const clientX = e.clientX;
    const clientY = e.clientY;
    const halfWidth = width / 2;
    const halfHeight = height / 2;
    const dx = clientX - left - halfWidth;
    const dy = clientY - top - halfHeight;
    const dxRatio = Math.min(1, Math.abs(dx) / halfWidth);
    const dyRatio = Math.min(1, Math.abs(dy) / halfHeight);

    const x = (dx / width) * 2 * -options.maxRotateDeg;
    const y = (dy / width) * 2 * options.maxRotateDeg;
    rotateX.set(x);
    rotateY.set(y);
    translateZ.set(width / options.translateZRatio);

    changeCardShineVariables({
      backgroundX: dxRatio * 100,
      backgroundY: dyRatio * 100,
      mouseX: ((clientX - left) * 100) / width,
      mouseY: ((clientY - top) * 100) / height,
      pointerFromCenter: Math.max(Math.abs(dxRatio), Math.abs(dyRatio)),
    });
  };

  const handleMouseEnter = () => {
    if (mouseLeaveTimeout.current) {
      clearTimeout(mouseLeaveTimeout.current);
    }
    setIsMouseEntered(true);
  };

  const handleMouseLeave = () => {
    if (mouseLeaveTimeout.current) {
      clearTimeout(mouseLeaveTimeout.current);
    }
    changeCardShineVariables({
      backgroundX: 0,
      backgroundY: 0,
      mouseX: 0,
      mouseY: 0,
      pointerFromCenter: 0,
    });
    mouseLeaveTimeout.current = setTimeout(() => {
      rotateX.set(0);
      rotateY.set(0);
      translateZ.set(0);
      setIsMouseEntered(false);
    }, options.mouseLeaveTimeout);
  };

  const style: CSSProperties & { '--mask-image': string } = {
    ...shiningProps?.style,
    '--mask-image': maskImage ? `url(${maskImage})` : 'none',
    willChange: 'transform, opacity, mask-image, background-image, filter',
    transitionProperty:
      'transform, mask-image, opacity, background-image, filter',
  };

  return (
    <Comp
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      ref={containerRef}
      {...containerProps}
    >
      <Slottable>{children}</Slottable>
      <motion.div
        className={cn(
          'pointer-events-none absolute inset-0 z-10 overflow-hidden rounded-md opacity-100 ease-linear',
          {
            'duration-200': isMouseEntered,
          },
          lightType === 'rainbow' && enabled && styles.rainbowShining,
          lightType === 'white' && enabled && styles.whiteShining
        )}
        ref={cardShineRef}
        style={style}
      />
    </Comp>
  );
};
